import BookDocumentInformation from 'types/BookDocumentInformation';
import BookLocation from 'types/BookLocation';
import scoreSearchResult from '../scoreSearchResult';

/**
 * @method resolveLunrSearchResults
 *
 * Takes Lunr search result data and translates it into an array of objects that we can use to display search results.
 *
 * @param {Object} options
 * @param {Book} options.book
 * @param {Array<Object>} options.lunrSearchResults
 * @param {Function} options.getDocumentInformation
 * @param {String} options.queryString
 * @param {Number} options.documentIndex - Limit resolution to a single document index. If null, resolve search results for all documents.
 * @returns {Array<Object>}
 */
const resolveLunrSearchResults = async ({
  book,
  getDocumentInformation,
  lunrSearchResults,
  queryString,
  limitResolutionToDocumentIndex = null,
  frameDocument = null,
}) => {
  const searchResults = [];

  for (const lunrSearchResult of lunrSearchResults) {
    const documentIndex = book.documents[lunrSearchResult.ref].index;

    // Skip this document if we're only resolving search results for a specific document.
    if (
      limitResolutionToDocumentIndex !== null &&
      limitResolutionToDocumentIndex !== documentIndex
    ) {
      continue;
    }

    // Obtain a document information object for the relevant document if we don't already have it.
    const bookDocumentInformation = await getDocumentInformation(
      documentIndex,
      limitResolutionToDocumentIndex !== null ? frameDocument : null,
      BookDocumentInformation.MODES.NO_DOM,
    );

    // This loops through the result set for each document
    const wordOffsets = [];
    for (const word in lunrSearchResult.matchData.metadata) {
      // Translate the (backend) word offsets that Lunr found into (frontend) word objects that we can use to build a search result.
      for (const [offset, length] of lunrSearchResult.matchData.metadata[word]
        .body.position) {
        const wordOffset = bookDocumentInformation.findWordOffset(
          word,
          offset,
          length,
          wordOffsets,
        );
        if (wordOffset) {
          wordOffsets.push(wordOffset);
        } else {
          console.warn('lost word offset');
        }
      }
    }

    // Combine word offsets in order to remove duplicates and merge adjacent results.
    const nestedWordOffsets = bookDocumentInformation.combineWordOffsets(
      ...wordOffsets,
    );

    // Compute context for the search results.
    const resolvedResults =
      bookDocumentInformation.buildSearchResults(nestedWordOffsets);

    // Reformat and build location for each result.
    const searchResultsGroup = [];
    for (const result of resolvedResults) {
      const wordOffsetTermStart = result.wordOffsets[0];
      const wordOffsetTermEnd =
        result.wordOffsets[result.wordOffsets.length - 1];

      const bookLocation = new BookLocation({
        book,
        documentIndex,
        wordOffset: [wordOffsetTermStart, wordOffsetTermEnd],
        event: 'Search result',
      });

      searchResultsGroup.push({
        marker: result.marker,
        snippet: result.html,
        foundString: result.foundString,
        queryString: queryString,
        score: scoreSearchResult(result.foundString, queryString),
        bookLocation,
        key: `search-result:${queryString.trim()}@${documentIndex}#${result.wordOffsets.join(
          ',',
        )}`,
      });
    }

    // Sort search results group by starting word offset.
    searchResultsGroup.sort((a, b) => {
      return a.bookLocation.wordOffset[0] - b.bookLocation.wordOffset[0];
    });

    // Add relative index property to each search result.
    searchResultsGroup.forEach((searchResult, index) => {
      searchResult.indexRelativeToDocument = index;
    });

    // Add search results group to search results.
    searchResults.push(...searchResultsGroup);
  }

  return searchResults;
};

export default resolveLunrSearchResults;
